import { ref, reactive, nextTick, onMounted, onUnmounted, type Ref } from 'vue';
import type { IExtremeMapRef } from 'src/models';
import type { LayoutStyles } from 'src/models/top';

const HEADER_HEIGHT_PX = 57;

export type LayoutContext = {
  refGeneralSearchBarRow: Ref<HTMLElement | undefined>;
  refPaneLeft: Ref<HTMLElement | undefined>;
  refPaneCenter: Ref<HTMLElement | undefined>;
  refMapTopMiscBar: Ref<HTMLElement | undefined>;
  refMapTopMiscBarLeft: Ref<HTMLElement | undefined>;
  refMapTopMiscBarRight: Ref<HTMLElement | undefined>;
  refMapSelectedElemInfoArea: Ref<HTMLElement | undefined>;
  refPaneRight: Ref<HTMLElement | undefined>;
  styles: LayoutStyles;
  resizeMap: () => void;
  paneResizeStopped: () => void;
};

export function useLayout(refExtremeMap: Ref<IExtremeMapRef | undefined>): LayoutContext {
  const refGeneralSearchBarRow = ref<HTMLElement>();
  const refPaneLeft = ref<HTMLElement>();
  const refPaneCenter = ref<HTMLElement>();
  const refMapTopMiscBar = ref<HTMLElement>();
  const refMapTopMiscBarLeft = ref<HTMLElement>();
  const refMapTopMiscBarRight = ref<HTMLElement>();
  const refMapSelectedElemInfoArea = ref<HTMLElement>();
  const refPaneRight = ref<HTMLElement>();

  const styles = reactive<LayoutStyles>({
    paneStyles: {
      sideMinWidth: '23%',
      sideMaxWidth: '30%',
      centerMinWidth: '40%',
      centerMaxWidth: '54%',
    },
    carListStyles: {
      minHeight: '20%',
      maxHeight: '38%',
    },
    movieListStyles: {
      minHeight: '40%',
      maxHeight: '40%',
    },
    tabStyles: {
      containerMinHeight: '40%',
      containerMaxHeight: '',
      contentMaxHeight: '40%',
    },
  });

  const resizeMap = () => {
    if (
      !refGeneralSearchBarRow.value ||
      !refMapTopMiscBarLeft.value ||
      !refMapTopMiscBarRight.value ||
      !refMapSelectedElemInfoArea.value ||
      !refExtremeMap.value
    ) return;

    const headerH = HEADER_HEIGHT_PX;
    const searchBarH = refGeneralSearchBarRow.value.clientHeight;
    // 地図の上部分の高さは固定値だが、スクロールしたら表示されている高さは減るから、それを考慮に入れてやる
    // (下にスクロールした状態で付箋を表示すると、スクロール下分だけ地図の高さは増える)
    const aboveMapH =
      Math.max(0, headerH + searchBarH - window.scrollY) +
      Math.max(refMapTopMiscBarLeft.value.clientHeight, refMapTopMiscBarRight.value.clientHeight);

    // 地図下部領域の高さは可変だが、地図の高さを決める時は最大でもこの数値までしかその領域の高さを考慮しないこととする.
    const mapSelectedElemInfoMaxH = 150;
    const belowMapH = Math.min(refMapSelectedElemInfoArea.value.clientHeight, mapSelectedElemInfoMaxH);

    const mapHeight = window.innerHeight - aboveMapH - belowMapH;
    refExtremeMap.value.setMapHeight(mapHeight);
    refExtremeMap.value.triggerResize();
  };

  const resizePanes = () => {
    if (!refPaneLeft.value || !refPaneCenter.value || !refPaneRight.value) return;

    const w = window.innerWidth - 36;
    const paneSideHardMaxWidth = 380;
    const paneCenterHardMinWidth = w - 380 * 2;

    styles.paneStyles.sideMinWidth = Math.min(Math.floor(w * 0.20), paneSideHardMaxWidth) + 'px';
    styles.paneStyles.sideMaxWidth = Math.min(Math.floor(w * 0.30), paneSideHardMaxWidth) + 'px';
    styles.paneStyles.centerMinWidth = Math.max(Math.floor(w * 0.40), paneCenterHardMinWidth) + 'px';
    styles.paneStyles.centerMaxWidth = Math.max(Math.floor(w * 0.60), paneCenterHardMinWidth) + 'px';
    // prefで変えるかもしれない
    const paneSideInitialWidth = Math.floor(w * 0.21) + 'px';
    const paneCenterInitialWidth = Math.floor(w * 0.60) + 'px';

    refPaneLeft.value.style.width = paneSideInitialWidth;
    refPaneCenter.value.style.width = paneCenterInitialWidth;
    refPaneRight.value.style.width = paneSideInitialWidth;

    nextTick(() => {
      resizeMap();
    });
  };

  const resizeLists = () => {
    const headerH = HEADER_HEIGHT_PX;
    const searchBarH = refGeneralSearchBarRow.value?.clientHeight || 0;
    const h = window.innerHeight - headerH - searchBarH;

    styles.carListStyles.minHeight = Math.floor(h * 0.20) + 'px';
    styles.carListStyles.maxHeight = Math.floor(h * 0.40) + 'px';
    styles.movieListStyles.minHeight = Math.floor(h * 0.49) + 'px';
    styles.movieListStyles.maxHeight = Math.floor(h * 0.49) + 'px';
    styles.tabStyles.containerMinHeight = Math.floor(h * 0.49) + 'px';
    styles.tabStyles.contentMaxHeight = Math.floor(h * 2.00) + 'px';
  };

  const onResize = () => {
    resizePanes();
    resizeLists();
  };

  const paneResizeStopped = () => {
    resizeMap();
  };

  onMounted(() => {
    onResize();
    window.addEventListener('resize', onResize);
  });

  onUnmounted(() => {
    window.removeEventListener('resize', onResize);
  });

  return {
    refGeneralSearchBarRow,
    refPaneLeft,
    refPaneCenter,
    refMapTopMiscBar,
    refMapTopMiscBarLeft,
    refMapTopMiscBarRight,
    refMapSelectedElemInfoArea,
    refPaneRight,
    styles,
    resizeMap,
    paneResizeStopped,
  };
}
